import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppState, store } from '../../../../../store/store';
import { NewsForgeErrorState } from '../../helpers/news-forge.helper';
import { DATA_QA } from '../../helpers/data-qa';
import '../../styles/news-forge.styles.scss';
import { updateNewsForgeContent } from '../../../../../store/action-creators/news-forge-action-creator';
import { INewsForgeTranslationContent, initialNewsForgeState, ITranslationResponse } from '../../models/news-forge.models';
import NewsForgeCategorySelect from '../subComponents/category';
import ExtractAndTranslateButton from '../subComponents/buttons/extract-and-translate-button';
import { GenerateArticleRequestStatuses } from '../../../ArticleAiGeneration/helpers/article-ai-generation.helper';
import { showCustomToast } from '../../../ArticleAiGeneration/components/subcomponents/schedules-listing-components/subcomponents/custom-toast-norification';
import TargetUrlInput from '../subComponents/url-link-field/url-link-field';
import NewsForgeOperationsContainer from '../subComponents/news-forge-text-container/news-forge-operations-container';
import NewsForgeTextContainer from '../subComponents/news-forge-text-container/news-forge-text-container';
import { NewsForgeRequestStatusTypes, useExtractAndTranslate, useNewsForgePolling } from '../../helpers/news-forge-requests.helper';
import ClearButton from '../subComponents/buttons/clear-button';

const NewsForgePanel: FunctionComponent = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const newsForgeContent = useSelector((state: AppState) => state.newsForge && state.newsForge.newsForgeContent);
	const [isExtracting, setIsExtracting] = useState(false);
	const [jobId, setJobId] = useState(null as string | null);
	const [isRegenerating, setIsRegenerating] = useState(false);
	const [errorState, setErrorState] = useState<NewsForgeErrorState>({
		category: false,
		target_url: false,
		original_content_title_limit: false,
		translated_content_title_limit: false,
		translated_content_title: false,
	});

	const { mutate: extractAndTranslate } = useExtractAndTranslate(dispatch, setJobId, setIsExtracting);

	const { data: pollingData } = useNewsForgePolling(jobId, {
		enabled: !!jobId && isExtracting,
		onSuccess: (response: ITranslationResponse) => {
			const currentContent = store.getState().newsForge && store.getState().newsForge.newsForgeContent;

			if (response.status === NewsForgeRequestStatusTypes.SUCCESSFUL) {
				const updatedContent: INewsForgeTranslationContent = {
					...currentContent,
					original_content: response.data && response.data.original_content,
					translated_content: response.data && response.data.translated_content,
				};

				dispatch(updateNewsForgeContent(updatedContent));
				showCustomToast(t('success'), t('news_forge_extraction_success'), GenerateArticleRequestStatuses.SUCCESS);
			} else if (response.status === NewsForgeRequestStatusTypes.FAILED) {
				showCustomToast(t('error'), t('news_forge_extraction_failed'), GenerateArticleRequestStatuses.ERROR);
			}

			setIsExtracting(false);
			setJobId(null);
		},
		onError: (err) => {
			showCustomToast(t('error'), err.message, GenerateArticleRequestStatuses.ERROR);
			setIsExtracting(false);
		},
	});

	useEffect(() => {
		return () => {
			dispatch(
				updateNewsForgeContent({
					...initialNewsForgeState,
				}),
			);
		};
	}, []);

	const onExtractAndTranslate = () => {
		setErrorState({
			...errorState,
			target_url: !newsForgeContent || !newsForgeContent.target_url,
		});

		if (newsForgeContent && newsForgeContent.target_url) {
			clearText();
			setIsRegenerating(false);
			extractAndTranslate(newsForgeContent.target_url);
		} else {
			showCustomToast(t('error_еxtracting_url'), t('fill_required_fields'), GenerateArticleRequestStatuses.ERROR);
		}
	};

	const clearText = () => {
		dispatch(
			updateNewsForgeContent({
				...newsForgeContent,
				original_content: null,
				translated_content: null,
			}),
		);
	};

	const hasExtractedContent =
		newsForgeContent &&
		newsForgeContent.original_content &&
		newsForgeContent.original_content.content &&
		newsForgeContent.translated_content &&
		newsForgeContent.translated_content.content;

	return (
		<div id={DATA_QA.NEWS_FORGE_WRAPPER} data-qa={DATA_QA.NEWS_FORGE_WRAPPER}>
			<div className='news-forge-header'>
				<h1>{t('url_translation')}</h1>
				<span className='header-description-label'>{t('url_translation_description')}</span>
			</div>
			<div className='input-fields-row'>
				<TargetUrlInput
					newsForgeContent={newsForgeContent}
					errorMessage={errorState.target_url ? t('target_url_warning') : undefined}
					errorState={errorState}
					setErrorState={setErrorState}
				/>
				<NewsForgeCategorySelect
					newsForgeContent={newsForgeContent}
					errorState={errorState}
					errorMessage={errorState.category ? t('mandatory_field') : undefined}
					setErrorState={setErrorState}
				/>
			</div>
			<div className='input-fields-row'>
				<ExtractAndTranslateButton text={t('extract_and_translate')} onClickAction={onExtractAndTranslate} title={t('extract_and_translate')} />
				<ClearButton text={t('clear')} title={t('clear')} onClickAction={clearText} />
			</div>
			{!isExtracting && hasExtractedContent ? (
				<NewsForgeTextContainer
					newsForgeContent={newsForgeContent}
					setJobId={setJobId}
					setIsExtracting={setIsExtracting}
					setErrorState={setErrorState}
					setIsRegenerating={setIsRegenerating}
				/>
			) : (
				<NewsForgeOperationsContainer isExtracting={isExtracting} newsForgeContent={newsForgeContent} isRegenerating={isRegenerating} />
			)}
		</div>
	);
};

export default NewsForgePanel;
